import axios from 'axios';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';
import enErrors from '../languages/enErrors.json';
import thErrors from '../languages/thErrors.json';
import ROUTE_NAME from '../routes/RouteName';
import { aesDecrypt, aesEncrypt } from '../utils/crypto';

const RESPONSE_TYPE = {
    JSON: 'json',
    BLOB: 'blob',
};

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const headers = {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,POST',
    'Cache-Control': 'no-store,no-cache,must-revalidate',
    Vary: 'Origin',
};

const api = axios.create({
    baseURL: baseUrl,
    headers: headers,
});

const notifySuccess = (message) => {
    toast.success(message, {
        duration: 5000,
        id: message,
    });
};

const notifyError = (message) => {
    toast.error(message, {
        duration: 5000,
        id: message,
    });
};

const logoutUser = () => {
    sessionStorage.clear();
    window.location.href = ROUTE_NAME.HOME;
};

const language = localStorage.getItem('language') === 'en' ? 'en' : 'th';

export async function getRequest(
    url,
    params,
    callbackResponse = null,
    callbackError = null,
    responseType = RESPONSE_TYPE.JSON
) {
    try {
        const response = await api.get(url, {
            params: { ...params },
            responseType,
            withCredentials: true,
        });

        if (callbackResponse) {
            callbackResponse(response);
        }

        return response;
    } catch (error) {
        if (callbackError) {
            callbackError(error);
        } else {
            if (error.response?.status === 401) {
                logoutUser();
                return;
            };

            // If user maliciously tries to avoid pre-investment screening check
            if (error.response?.status === 512) {
                toast.loading(language === 'en' ? enErrors.pre_screening_failure : thErrors.pre_screening_failure, {
                    duration: 5000,
                    icon: '❌',
                    toastId: 'loadingToast',
                });

                setTimeout(() => {
                    toast.dismiss('loadingToast');
                    logoutUser();
                }, 5000);

                return;
            };

            if (enErrors.hasOwnProperty(error.response.data.error)) {
                notifyError(language === 'en' ? enErrors[error.response.data.error] : thErrors[error.response.data.error]);

                return;
            };

            notifyError(
                language === 'en' ? enErrors.general_error : thErrors.general_error
            );
        }
    }
}

export async function postRequest(
    url,
    params,
    data = {},
    callbackResponse = null,
    callbackError = null,
    responseType = RESPONSE_TYPE.JSON
) {
    try {
        const response = await api.post(url, data, {
            params: { ...params },
            responseType,
            withCredentials: true,
        });
        const { data: responseData } = response.data;

        if (callbackResponse) {
            callbackResponse(responseData);
        }

        return responseData;
    } catch (error) {
        if (callbackError) {
            callbackError(error);
        } else {
            if (error.response?.status === 401) {
                logoutUser();
                return;
            };

            // If user maliciously tries to avoid pre-investment screening check
            if (error.response?.status === 512) {
                toast.loading(language === 'en' ? enErrors.pre_screening_failure : thErrors.pre_screening_failure, {
                    duration: 5000,
                    icon: '❌',
                    toastId: 'loadingToast',
                });

                setTimeout(() => {
                    toast.dismiss('loadingToast');
                    logoutUser();
                }, 5000);

                return;
            };

            if (enErrors.hasOwnProperty(error.response?.data?.error)) {
                notifyError(language === 'en' ? enErrors[error.response?.data?.error] : thErrors[error.response?.data?.error]);

                return;
            };

            notifyError(
                language === 'en' ? enErrors.general_error : thErrors.general_error
            );
        }
    }
}

export async function postLoginRequest(
    url,
    data = {},
    callbackResponse,
    responseType = RESPONSE_TYPE.JSON
) {
    const requestOptions = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'GET,POST',
            'Cache-Control': 'no-store,no-cache,must-revalidate',
            Vary: 'Origin',
        },
        body: JSON.stringify(data),
    };

    let fetchUrl = baseUrl + url;

    return fetch(fetchUrl, requestOptions)
        .then((response) => {
            if (!response.ok) {
                throw new Error(language === 'en' ? enErrors.wrong_otp_code : thErrors.wrong_otp_code);
            }
            return response.json();
        })
        .then((responseData) => {
            if (callbackResponse) {
                return callbackResponse(responseData);
            }
        })
        .catch((error) => {
            notifyError(enErrors.wrong_otp_code);
        });
}

export async function getRefreshTokenRequest() {
    const decryptedRefreshToken = aesDecrypt(sessionStorage.getItem('refresh_token') || '');

    if (decryptedRefreshToken === '') {
        logoutUser();
    }

    try {
        const response = await api.post(
            'api/refresh-token',
            {
                grant_type: 'refresh_token',
                client_id: process.env.REACT_APP_CLIENT_ID,
                client_secret: process.env.REACT_APP_CLIENT_SECRET,
                refresh_token: decryptedRefreshToken,
            }, {
            responseType: RESPONSE_TYPE.JSON
        });

        const { access_token, refresh_token } = response.data;

        const encryptedAccessToken = aesEncrypt(access_token);
        sessionStorage.setItem('access_token', encryptedAccessToken);
        const encryptedRefreshToken = aesEncrypt(refresh_token);
        sessionStorage.setItem('refresh_token', encryptedRefreshToken);
    } catch (error) {
        if (enErrors.hasOwnProperty(error.response.data.error)) {
            notifyError(language === 'en' ? enErrors[error.response.data.error] : thErrors[error.response.data.error]);

            return;
        }

        notifyError(
            language === 'en' ? enErrors.general_error : thErrors.general_error
        );
    }
}

export async function getFileRequest(
    url,
    params,
    filename,
    callbackError = null,
    responseType = RESPONSE_TYPE.BLOB
) {
    try {
        const response = await api.get(url, {
            params: { ...params },
            responseType,
        });

        const contentType = response.headers['content-type'];
        let blobType = 'application/octet-stream';

        if (contentType.includes('application/vnd.ms-excel') || contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
            blobType = 'application/vnd.ms-excel';
        } else if (contentType.includes('application/pdf')) {
            blobType = 'application/pdf';
        } else if (contentType.includes('image/jpeg')) {
            blobType = 'image/jpeg';
        } else if (contentType.includes('image/png')) {
            blobType = 'image/png';
        }

        const blob = new Blob([response.data], {
            type: blobType,
        });
        saveAs(blob, filename);
    } catch (error) {
        if (callbackError) {
            callbackError(error);
        } else {
            if (enErrors.hasOwnProperty(error.response.data.error)) {
                notifyError(language === 'en' ? enErrors[error.response.data.error] : thErrors[error.response.data.error]);
                return;
            }

            notifyError(
                language === 'en' ? enErrors.download_file_failure : thErrors.download_file_failure
            );
        }
    }
}

export async function postFileRequest(
    url,
    params,
    file,
    callbackResponse = null,
    callbackError = null,
    responseType = RESPONSE_TYPE.JSON
) {
    try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(url, formData, {
            params: {
                ...params,
            },
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            responseType,
        });

        if (callbackResponse) {
            callbackResponse(response.data);
        }
    } catch (error) {
        if (callbackError) {
            callbackError(error);
        } else {
            notifyError(
                error.response.data.message ?? error.response.data.error
            );
        }
    }
}

api.interceptors.request.use(
    (config) => {
        const accessToken = aesDecrypt(sessionStorage.getItem('access_token'));
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
);
